import { Tooltip } from 'components/ui/tooltip';
import { ReactNode, useRef, useState } from 'react';

type Props = {
    /**
     * What to reveal. Omit it and the rendered text itself is used, which is the right default:
     * a hand-passed title is a second copy of what the cell renders, and drifts from it — a party
     * name rendered with its number would otherwise reveal only half of what was cut.
     */
    title?: ReactNode;
    children: ReactNode;
};

/**
 * Reveals a value that its container cut short, and stays out of the way when it did not.
 *
 * Use this for a ONE-LINE value in a fixed-width host — a managed table cell. {@link LineClamp} is
 * the multi-line counterpart, and the two are not interchangeable: `LineClamp` measures height
 * against a `-webkit-line-clamp`, which counts line boxes, so it silently does nothing when its
 * child renders as flex (a `PartyName`, a link that lays itself out) and it cannot report a
 * horizontal cut at all.
 *
 * Overflow is therefore read across the subtree rather than off one element. Whichever node ends up
 * being the one that shrinks — this wrapper for plain text, a link's own box, the name span deep
 * inside a party name — is the node whose `scrollWidth` exceeds its client width, and only that
 * node knows. The walk runs on hover, over one cell's worth of DOM.
 */
export function EllipsisTooltip({ title, children }: Props): JSX.Element {
    const [revealed, setRevealed] = useState<ReactNode>(null);
    const contentRef = useRef<HTMLSpanElement>(null);

    // Opening is refused outright when nothing was cut short, so a cell that fits shows no bubble.
    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setRevealed(null);
            return;
        }
        const element = contentRef.current;
        if (!element) {
            return;
        }
        const isCutShort = [element, ...element.querySelectorAll<HTMLElement>('*')].some(
            (node) => node.scrollWidth - node.clientWidth > 1
        );
        if (isCutShort) {
            setRevealed(title ?? element.innerText);
        }
    };

    return (
        <Tooltip
            content={revealed}
            open={revealed !== null}
            onOpenChange={handleOpenChange}
            delay={500}
            trackCursorAxis="both"
            disableHoverablePopup
        >
            <span ref={contentRef} className="block min-w-0 truncate">
                {children}
            </span>
        </Tooltip>
    );
}
